/**
 * FreeAgent Authentication Component
 * 
 * Shows connection status and provides connect/disconnect functionality
 */

import React, { useEffect, useState } from 'react';
import { Box, Card, Typography, Button, Stack, Alert, Chip } from '@mui/joy';
import { FaCheckCircle, FaTimesCircle, FaPlug, FaUnlink } from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import { isAuthenticated, initiateAuthorization, disconnect, isFreeAgentConfigured, getRedirectUri } from '../freeagent/auth';
import { isFreeAgentSyncEnabled } from '../config/freeagentConfig';

export const FreeAgentAuth: React.FC = () => {
  const [connected, setConnected] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    setLoading(true);
    try {
      const authStatus = await isAuthenticated();
      setConnected(authStatus);
    } catch (error) {
      console.error('Failed to check FreeAgent connection:', error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    try {
      initiateAuthorization();
    } catch (error: any) {
      alert(`Failed to initiate connection: ${error.message}`);
    }
  };

  const handleDisconnect = () => {
    if (window.confirm('Are you sure you want to disconnect from FreeAgent? This will prevent invoice syncing.')) {
      disconnect();
      setConnected(false);
      alert('Disconnected from FreeAgent');
    }
  };

  const configured = isFreeAgentConfigured();
  const syncEnabled = isFreeAgentSyncEnabled();

  if (loading) {
    return (
      <Card>
        <Typography>Checking FreeAgent connection...</Typography>
      </Card>
    );
  }

  return (
    <Card>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography level="h4">FreeAgent Connection</Typography>
          {connected ? (
            <Chip color="success" startDecorator={<IconWrapper icon={FaCheckCircle} />}>
              Connected
            </Chip>
          ) : (
            <Chip color="neutral" startDecorator={<IconWrapper icon={FaTimesCircle} />}>
              Not Connected
            </Chip>
          )}
        </Box>

        {!configured && (
          <Alert color="warning">
            <Typography level="body-sm">
              FreeAgent is not fully configured. Please set REACT_APP_FA_CLIENT_ID and 
              REACT_APP_USER_AGENT environment variables in Vercel (client-side).
              The client secret (FA_CLIENT_SECRET) is handled securely server-side.
            </Typography>
          </Alert>
        )}

        {configured && !syncEnabled && (
          <Alert color="neutral">
            <Typography level="body-sm">
              FreeAgent sync is disabled globally. Set REACT_APP_FA_SYNC_ENABLED=true to enable.
            </Typography>
          </Alert>
        )}

        {connected ? (
          <>
            <Typography level="body-sm" color="success">
              Your FreeAgent account is connected. Invoices can be synced to FreeAgent when 
              organisation-level sync is enabled.
            </Typography>
            <Button
              variant="outlined"
              color="danger"
              startDecorator={<IconWrapper icon={FaUnlink} />}
              onClick={handleDisconnect}
            >
              Disconnect
            </Button>
          </>
        ) : (
          <>
            <Typography level="body-sm">
              Connect your FreeAgent account to enable invoice syncing. You'll be redirected 
              to FreeAgent to authorize the connection.
            </Typography>
            <Button
              variant="solid"
              color="primary"
              startDecorator={<IconWrapper icon={FaPlug} />}
              onClick={handleConnect}
              disabled={!configured || checking}
            >
              Connect to FreeAgent
            </Button>
          </>
        )}

        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography level="body-sm" sx={{ fontWeight: 'bold', mb: 1 }}>
            How it works:
          </Typography>
          <Typography level="body-sm" component="ul" sx={{ pl: 2 }}>
            <li>Connect your FreeAgent account once</li>
            <li>Enable FreeAgent sync at the organisation level</li>
            <li>Invoices will automatically sync when status changes to sent/paid/rejected</li>
          </Typography>
        </Box>

        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography level="body-sm" sx={{ fontWeight: 'bold', mb: 1 }}>
            Redirect URI (for FreeAgent OAuth configuration):
          </Typography>
          <Typography 
            level="body-sm" 
            sx={{ 
              fontFamily: 'monospace', 
              backgroundColor: 'neutral.50', 
              p: 1, 
              borderRadius: 'sm',
              wordBreak: 'break-all'
            }}
          >
            {getRedirectUri()}
          </Typography>
          <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
            Make sure this exact URL is configured in your FreeAgent OAuth app settings.
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
};


